import { useForm, SubmitHandler } from "react-hook-form";
import { useState, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormSubmit } from "@/components/FormSubmit";
import { Textarea } from "@/components/Textarea";
import { RangeSlider } from "@/components/RangeSlider";
import { SecondaryButton } from "@/components/buttonVariants";
import { CheckIcon } from "@/components/icons/icons";
import { api, AchieverQuestionOut, AchieverQuestionUpdate } from "@/services/api";
import { useErrorHandler } from "@/context/ErrorHandlerContext";
import { useToast } from "@/hooks/useToast";
import { PROFILE_LOAD_ERROR } from "@/utils/toastMessages";
import { useNavigate } from "react-router-dom";
import { profileStepCompleted, profileCompleted } from "@/utils/tracking";
import { useAuth } from "@/hooks/useAuth";
import { getPrimaryRole } from "@/utils/roleUtils";
import { useUpdateOnboardingStep } from "@/hooks/useUpdateOnboardingStep";
import { CheckCircleFilledIcon } from "@/components/icons/icons";
import { FieldLabel } from "@/components/FieldLabel";

const BIO_MAX = 500;
const NORTH_STAR_MAX = 500;

const growthSchema = z.object({
  bio: z
    .string()
    .min(1, "Please share a few highlights about your experience")
    .max(BIO_MAX, `Please limit to ${BIO_MAX} characters`),
  northStar: z
    .string()
    .min(1, "Please share what you'd like to achieve next")
    .max(NORTH_STAR_MAX, `Please limit to ${NORTH_STAR_MAX} characters`),
});

type GrowthFormInputs = z.infer<typeof growthSchema>;

/**
 * Achiever-only Step 3 ("Your Growth") — the final signup step for achievers.
 * Experience Highlights / Bio + North Star goal + the questionnaire sliders,
 * then "Complete Sign Up". Reuses Textarea + RangeSlider common components.
 */
export const AchieverGrowthForm = () => {
  const [loading, setLoading] = useState(false);
  const [achieverId, setAchieverId] = useState<string | null>(null);
  const [profileExists, setProfileExists] = useState(false);
  const [questions, setQuestions] = useState<AchieverQuestionOut[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<Record<number, number>>({});
  const [originalAnswers, setOriginalAnswers] = useState<Record<number, number>>({});
  const [touchedQuestions, setTouchedQuestions] = useState<Set<number>>(new Set());
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<GrowthFormInputs>({
    resolver: zodResolver(growthSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: { bio: "", northStar: "" },
  });

  const { showToast } = useToast();
  const handleApiError = useErrorHandler();
  const navigate = useNavigate();
  const { updateStep } = useUpdateOnboardingStep();

  useEffect(() => {
    const fetchData = async () => {
      setQuestionsLoading(true);
      try {
        const achiever = await api.achievers.getAchiever();
        setAchieverId(achiever.id);

        try {
          const existing = (await api.achievers.getProfile(achiever.id)) as Record<string, unknown>;
          if (existing && Object.keys(existing).length > 0) {
            setProfileExists(true);
            if (existing.bio) setValue("bio", existing.bio as string);
            if (existing.goalsAndChallenges)
              setValue("northStar", existing.goalsAndChallenges as string);
          }
        } catch {
          // No existing profile — first time
        }

        const qs = await api.achievers.getQuestions(achiever.id);
        setQuestions(qs);
        const prefilled: Record<number, number> = {};
        qs.forEach((q) => {
          if (q.value != null) prefilled[q.id] = Number(q.value);
        });
        setQuestionAnswers(prefilled);
        setOriginalAnswers({ ...prefilled });
      } catch {
        showToast({ type: "error", message: PROFILE_LOAD_ERROR });
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bio = watch("bio") || "";
  const northStar = watch("northStar") || "";

  const onsubmit: SubmitHandler<GrowthFormInputs> = async (data) => {
    setLoading(true);
    try {
      const id = achieverId ?? (await api.achievers.getAchiever()).id;

      const existingProfile = (await api.achievers.getProfile(id)) as Record<string, unknown>;
      await api.achievers.updateProfile(id, {
        ...existingProfile,
        bio: data.bio || "",
        goalsAndChallenges: data.northStar,
      });
      setProfileExists(true);

      if (questions.length > 0) {
        await Promise.all(
          questions
            .filter((q) => (questionAnswers[q.id] ?? 50) !== (originalAnswers[q.id] ?? 50))
            .map((q) =>
              api.achievers.updateQuestion(id, q.id, {
                value: String(questionAnswers[q.id] ?? 50),
              } satisfies AchieverQuestionUpdate),
            ),
        );
      }

      if (user) {
        profileStepCompleted({ step_name: "growth_completed", user_role: getPrimaryRole(user) });

        const signupTimestamp = localStorage.getItem("signup_timestamp");
        const timeToCompleteSeconds = signupTimestamp
          ? Math.floor((Date.now() - parseInt(signupTimestamp)) / 1000)
          : 0;
        profileCompleted({ time_to_complete_seconds: timeToCompleteSeconds, user_role: user.role });
        localStorage.removeItem("signup_timestamp");
      }

      await updateStep("complete");
      // Reached here means either an organic (no-join) signup, or an achiever
      // who arrived from a program link (emailed invite or a shared public
      // URL) and was routed through the full 3-step flow.
      //
      // The join case lands on that program's detail page, NOT /home: they
      // clicked a link about one specific program, so dropping them on a
      // generic dashboard loses the thing they came for and leaves them to
      // find it again by hand. /explore/:externalId shows the program with its
      // Enroll button, which is the next step they actually want.
      const joinProgramId = localStorage.getItem("program_join_id") || undefined;
      navigate(joinProgramId ? `/explore/${joinProgramId}` : "/explore");
    } catch (err: unknown) {
      handleApiError(err, growthSchema);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onsubmit)} className="flex flex-col gap-6">
      {/* Heading */}
      <div className="flex flex-col gap-2">
        <h1 className="text-[28px] font-bold leading-tight tracking-[0.3px] text-theme-text-heading">
          {profileExists ? "Update Your Profile" : "Build Your Profile"}
        </h1>
        <p className="text-18 text-theme-text-muted leading-7 tracking-[-0.44px]">
          Tell us about your professional background
        </p>
        {user?.email && (
          <div className="flex items-center gap-1.5">
            <CheckCircleFilledIcon className="text-theme-text-success" />
            <span className="text-12 font-medium text-theme-text-success">
              Signed in as {user.email}
            </span>
          </div>
        )}
      </div>

      {/* Experience Highlights / Bio */}
      <div className="flex flex-col gap-2">
        <FieldLabel required htmlFor="bio">
          Experience Highlights / Bio
        </FieldLabel>
        <div className="relative">
          <Textarea
            id="bio"
            richText
            {...register("bio")}
            maxLength={BIO_MAX}
            placeholder="Share key achievements, unique experiences, or what makes you stand out…"
            rows={5}
          />
        </div>
        <p className="text-12 text-theme-text-muted">
          {bio.length}/{BIO_MAX} characters
        </p>
        {errors.bio && <p className="error-message">{errors.bio.message}</p>}
      </div>

      {/* North Star goal */}
      <div className="flex flex-col gap-2">
        <FieldLabel required htmlFor="northStar">
          What would you like to Achieve next?
        </FieldLabel>
        <div className="relative">
          <Textarea
            id="northStar"
            richText
            {...register("northStar")}
            maxLength={NORTH_STAR_MAX}
            placeholder="Share your North Star goal or your long term goal vision…"
            rows={5}
          />
        </div>
        <p className="text-12 text-theme-text-muted">
          {northStar.length}/{NORTH_STAR_MAX} characters
        </p>
        {errors.northStar && <p className="error-message">{errors.northStar.message}</p>}
      </div>

      {/* Questionnaire sliders */}
      {questionsLoading && <p className="text-12 text-theme-text-muted">Loading questions…</p>}
      {!questionsLoading && questions.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <p className="text-14 font-semibold text-theme-text-heading">
              Where are you on these scales?
            </p>
            <p className="text-12 text-theme-text-muted leading-[1.55]">
              Drag each slider to reflect your natural tendencies — these help us find your best
              expert match.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {questions.map((q) => (
              <RangeSlider
                key={q.id}
                question={q.question}
                leftLabel={q.leftLabel}
                rightLabel={q.rightLabel}
                isPristine={!touchedQuestions.has(q.id)}
                value={questionAnswers[q.id] ?? 50}
                onChange={(val) => {
                  setQuestionAnswers((prev) => ({ ...prev, [q.id]: val }));
                  setTouchedQuestions((prev) => new Set(prev).add(q.id));
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="flex items-stretch gap-4 pt-2 h-12 md:h-[58px]">
        <SecondaryButton
          className="text-14 md:text-16 text-back-btn-text text-center font-inter font-semibold"
          onClick={() => navigate("/signup/2")}
        >
          Back
        </SecondaryButton>
        <div className="flex-1">
          <FormSubmit
            isLoading={loading}
            disabled={loading}
            variant="primaryGradient"
            className="h-12 md:h-14 text-14 md:text-16 font-semibold rounded-[14px]"
            rightIcon={<CheckIcon className="text-white" />}
          >
            Complete Sign Up
          </FormSubmit>
        </div>
      </div>
    </form>
  );
};
